import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { useSignupUrl } from '@/hooks/useSignupUrl';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { trackLandingCtaClicked } from '@/lib/analytics';
import { useTranslation } from '@/i18n/i18n';

const proofItems = [
  {
    title: 'biura.hero.proof1.title',
    description: 'biura.hero.proof1.description',
  },
  {
    title: 'biura.hero.proof2.title',
    description: 'biura.hero.proof2.description',
  },
  {
    title: 'biura.hero.proof3.title',
    description: 'biura.hero.proof3.description',
  },
] as const;

export function BiuraHero() {
  const sectionRef = useLandingSectionTracking('biura_hero');
  const signupUrl = useSignupUrl({ entityType: 'biuro', source: 'biura_hero' });
  const { t } = useTranslation();

  return (
    <section
      ref={sectionRef}
      className="px-6 md:px-8 pt-16 pb-14 bg-background border-b border-border"
      aria-labelledby="biura-hero-heading"
    >
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
        <div>
          <Badge variant="outline" className="mb-5">
            {t('biura.hero.badge')}
          </Badge>
          <h1
            id="biura-hero-heading"
            className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight"
          >
            {t('biura.hero.heading')}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
            {t('biura.hero.subheading')}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button size="lg" className="h-14 px-7 font-bold rounded-xl" asChild>
              <a
                href={signupUrl}
                onClick={() =>
                  trackLandingCtaClicked('biura_hero_primary', t('biura.hero.cta'), {
                    entity_type: 'biuro',
                  })
                }
              >
                {t('biura.hero.cta')}
              </a>
            </Button>
            <Button size="lg" variant="outline" className="h-14 px-7 rounded-xl" asChild>
              <a href="#biura-pricing">{t('biura.hero.secondaryCta')}</a>
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-lg">
          <p className="font-mono text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-5">
            {t('biura.hero.panelTitle')}
          </p>
          <div className="space-y-4">
            {proofItems.map((item) => (
              <div key={item.title} className="flex gap-3">
                <Check className="size-5 text-success mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-foreground">{t(item.title)}</p>
                  <p className="text-sm text-muted-foreground mt-1">{t(item.description)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
